import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  Toggle,
  useBlotterTableExtrasMenu,
  useCallbackRef,
} from '@talos/kyoko';
import { CONTROL_TOOLTIPS } from '../tooltips';
import type { CreditTabProps } from './types';
import { useCreditBlotterFoundation } from './useCreditBlotterFoundation';

export interface ExposuresBlotterParams {
  blotterID: string;
  tab: CreditTabProps;
  onUpdateTab: (tab: CreditTabProps) => void;
  initialIsOpen?: boolean;
  onCloneTab: () => void;
}

export function CreditBlotter({ blotterID, tab, onUpdateTab, onCloneTab, initialIsOpen }: ExposuresBlotterParams) {
  const {
    blotterTable,
    creditMenuDialogs,
    filterBuilderAccordion,
    showTotals,
    showZeroBalances,
    updateShowTotals,
    updateShowZeroBalances,
  } = useCreditBlotterFoundation({
    blotterID,
    tab,
    onUpdateTab,
    initialIsOpen,
    enableContextMenus: true,
    enableCreditMenu: true,
    enableRowSelection: true,
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <Toggle
              tooltip={CONTROL_TOOLTIPS.showZeroBalances}
              tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
              size={FormControlSizes.Small}
              checked={showZeroBalances}
              onChange={updateShowZeroBalances}
              label="Show Zero Balances"
            />
            <Toggle
              size={FormControlSizes.Small}
              checked={showTotals}
              onChange={updateShowTotals}
              label="Show Totals"
            />

            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            <Button
              startIcon={IconName.Duplicate}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleCloneTab}
            >
              Clone Tab
            </Button>
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {creditMenuDialogs}
    </>
  );
}
